import type { ErrorInfo, ReactNode } from "react";
import { Component } from "react";

import { MESSAGES } from "@/i18n/messages";
import { DEFAULT_LOCALE } from "@/i18n/types";
import { logger } from "@/lib/logger";

interface ErrorBoundaryCopy {
  eyebrow: string;
  hint: string;
  retryLabel: string;
  title: string;
}

interface ErrorBoundaryProps {
  children: ReactNode;
  copy?: ErrorBoundaryCopy;
  fallback?: ReactNode | ((error: Error, reset: () => void) => ReactNode);
  onError?: (error: Error, info: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  error: Error | null;
}

const defaultCopy: ErrorBoundaryCopy = {
  eyebrow: MESSAGES[DEFAULT_LOCALE].errors.boundaryEyebrow,
  hint: MESSAGES[DEFAULT_LOCALE].errors.boundaryHint,
  retryLabel: MESSAGES[DEFAULT_LOCALE].actions.tryAgain,
  title: MESSAGES[DEFAULT_LOCALE].errors.boundaryTitle,
};

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    logger.error("ErrorBoundary caught an error", {
      error,
      componentStack: info.componentStack,
    });
    this.props.onError?.(error, info);
  }

  reset = (): void => {
    this.setState({ error: null });
  };

  render(): ReactNode {
    const { error } = this.state;

    if (!error) {
      return this.props.children;
    }

    const { fallback } = this.props;
    const copy = this.props.copy ?? defaultCopy;

    if (typeof fallback === "function") {
      return fallback(error, this.reset);
    }

    if (fallback) {
      return fallback;
    }

    return (
      <div
        className="mx-auto max-w-2xl space-y-4 px-6 py-16 text-center"
        role="alert"
      >
        <p className="eyebrow">{copy.eyebrow}</p>
        <h1 className="text-3xl font-semibold text-foreground">{copy.title}</h1>
        <p className="text-sm leading-6 text-muted-foreground">{copy.hint}</p>
        <button
          className="inline-flex min-h-10 items-center justify-center rounded-full border border-border px-4 py-2 text-sm font-medium transition hover:border-primary hover:text-primary"
          onClick={this.reset}
          type="button"
        >
          {copy.retryLabel}
        </button>
      </div>
    );
  }
}
